<template>
  <nav class="manager-sidebar">
    <!-- WORKSPACE SWITCHER -->

    <div class="manager-sidebar__workspaceswitcher">
      <button
        class="manager-sidebar__workspaceswitcher__button manager-sidebar__icontext"
        :class="{ '-is-open': workspaceSwitcher.open }"
        type="button"
        @click="workspaceSwitcher.open = !workspaceSwitcher.open"
      >
        <lucide-icon
          v-if="workspaceSwitcher.open"
          name="x"
          class="manager-sidebar__workspaceswitcher__icon"
        />
        <lucide-icon
          v-else
          name="menu"
          class="manager-sidebar__workspaceswitcher__icon"
        />

        <span v-if="isSettings">{{ $t("labels.settings") }}</span>
        <template v-else-if="openWorkspace">
          <span>
            <template v-if="openWorkspace.organizationSlug">
              {{ openWorkspace.organizationName }} / {{ openWorkspace.name }}
            </template>
            <template v-else>
              {{ $t("labels.personalWorkspace") }}
            </template>
          </span>
        </template>
      </button>
    </div>
    <transition name="transition-fade-down">
      <div
        v-show="workspaceSwitcher.open"
        class="manager-sidebar__workspaceswitcher__list"
      >
        <div>
          <div class="manager-sidebar__block pt-0">
            <workspace-switcher-entry
              v-if="workspaceContext.userWorkspace"
              :workspace="workspaceContext.userWorkspace"
              :current-workspace-id="openWorkspace?.id ?? null"
              @close="workspaceSwitcher.open = false"
            />
          </div>
          <div
            v-for="org in workspaceOrganizations"
            :key="org.slug"
            class="manager-sidebar__block"
          >
            <h4
              class="manager-sidebar__subheading mb-3 mt-0 manager-sidebar__icontext"
            >
              <lucide-icon name="organization" class="text-xl" />
              <span>{{ org.name }}</span>
            </h4>
            <template v-if="org.teams.length > 0">
              <sortable-list
                :items="org.teams"
                :sortable="
                  org?.membership?.role?.canManageOrganization ?? false
                "
                @reorder-item="onTeamReorder"
              >
                <template #default="{ item: workspace }">
                  <workspace-switcher-entry
                    :workspace="workspace"
                    :current-workspace-id="openWorkspace?.id ?? null"
                    @close="workspaceSwitcher.open = false"
                  />
                </template>
              </sortable-list>
            </template>
            <template v-else>
              <p class="manager-sidebar__message mb-0 mt-3">
                {{ $t("messages.notInOrganizationTeams") }}
              </p>
            </template>
          </div>

          <div class="manager-sidebar__block">
            <router-link
              v-slot="{ href, navigate, isActive }"
              :to="{ name: 'settings-profile' }"
              custom
            >
              <a
                class="workspacefolders-nav__entry workspacefolders-nav__link manager-sidebar__icontext"
                :href="href"
                :class="{ 'is-active': isActive || isSettings }"
                @click.prevent="navigateAndClose(navigate)"
              >
                <lucide-icon
                  name="settings"
                  style="font-size: 1.25rem"
                /><span>{{ $t("labels.settings") }}</span>
              </a>
            </router-link>
          </div>

          <div
            v-if="
              workspaceContext.userTeams.length === 0 &&
              organizationContext.userOrganizations.length == 0
            "
            class="pt-4"
          >
            <div
              class="manager-sidebar__icontext manager-sidebar__message mb-4"
            >
              <lucide-icon name="organization" class="text-xl" />
              <p class="mt-0">
                {{ $t("texts.noTeams") }}
              </p>
            </div>
            <div class="manager-sidebar__message">
              <router-link
                :to="{ name: 'order-subscription' }"
                class="button"
              >
                {{ $t("actions.orderOrganizationSubscription") }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <div class="manager-sidebar__main">
      <slot></slot>

      <footer class="manager-sidebar__footer">
        <img src="@/assets/menu_image.svg" class="manager-sidebar__image" />
        <div class="manager-sidebar__footerbar">
          <router-link :to="{ name: 'imprint' }">
            {{ $t("labels.imprint") }}
          </router-link>
          <a :href="links.privacy" target="_blank">
            {{ $t("labels.privacyPolicy") }}
          </a>

          <button
            class="manager-sidebar__footerextrabutton"
            type="button"
            @click="openFooterExtraLinks($event)"
          >
            <svg width="30" height="20" viewBox="0 0 30 20">
              <circle cx="15" cy="10" r="3" fill="currentColor" />
              <circle cx="6" cy="10" r="3" fill="currentColor" />
              <circle cx="24" cy="10" r="3" fill="currentColor" />
            </svg>
          </button>

          <!-- FOLDER CONTEXT MENU -->
          <context-menu
            v-if="footerLinks.open"
            :x="footerLinks.pos.x"
            :y="footerLinks.pos.y"
            y-alignment="bottom"
            @close="onFooterContextMenuClose"
          >
            <a :href="links.agb" target="_blank" class="context-menu__entry">
              {{ $t("labels.conditions") }}
            </a>
            <a :href="links.terms" target="_blank" class="context-menu__entry">
              {{ $t("labels.termsOfUse") }}
            </a>
          </context-menu>
        </div>
      </footer>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { reactive, computed } from "vue";

import LucideIcon from "@core/ui/LucideIcon.vue";
import WorkspaceSwitcherEntry from "@manager/common/WorkspaceSwitcherEntry.vue";
import SortableList from "@core/ui/SortableList.vue";
import ContextMenu from "@core/ui/ContextMenu.vue";

import useTermsLinks from "./useTermsLinks";
import { isPersonalWorkspace, useWorkspaces } from "@/api/workspaces.store";
import { useOrganizations } from "@manager/organizations/organizations.store";

import type { OrganizationWorkspace, WorkspaceMember } from "@/api/Workspace";

const props = withDefaults(
  defineProps<{
    isSettings?: boolean;
    organizationSlug?: string;
    teamSlug?: string;
    folderSlug?: string;
  }>(),
  {
    isSettings: false,
    organizationSlug: undefined,
    teamSlug: undefined,
    folderSlug: undefined
  }
);

const workspaceContext = reactive(useWorkspaces(props));
const organizationContext = reactive(useOrganizations(props));

const openWorkspace = computed(() => {
  if (props.isSettings) return null;
  else return workspaceContext.currentWorkspace;
});

const workspaceOrganizations = computed(() => {
  const orgs = workspaceContext.userTeams.reduce((orgs, workspace) => {
    let org = orgs.find((o) => o.slug == workspace?.organizationSlug);
    if (!workspace) return orgs;
    if (isPersonalWorkspace(workspace)) return orgs;

    if (org == null) {
      org = {
        slug: workspace.organizationSlug,
        name: workspace.organizationName,
        teams: []
      };
      orgs.push(org);
    }

    org.teams.push(workspace);

    return orgs;
  }, [] as { slug: string; name: string; teams: OrganizationWorkspace[]; membership?: WorkspaceMember }[]);

  for (const org of organizationContext.userOrganizations) {
    if (org.expired) {
      continue;
    }
    const orgEntry = orgs.find((o) => o.slug == org.slug);
    if (orgEntry == null) {
      orgs.push({
        ...org,
        teams: []
      });
    } else {
      orgEntry.membership = org.membership;
    }
  }

  return orgs;
});

function onTeamReorder(orderEvent: { id: number; order: number }) {
  const id = orderEvent.id;
  const order = orderEvent.order;

  const team = workspaceContext.teams.find((f) => f?.id == id);

  if (!team) return;

  organizationContext.moveTeam({
    ...team,
    order
  });
}

// ================================
// VIEW STATE

const workspaceSwitcher = reactive({
  open: false
});

function navigateAndClose(navigate: () => void) {
  navigate();
  workspaceSwitcher.open = false;
}

// ================================
// FOOTER LINKS

const { links } = useTermsLinks();

const footerLinks = reactive({
  open: false,
  pos: {
    x: 0,
    y: 0
  }
});

function openFooterExtraLinks($event: Event) {
  const target = $event.target as HTMLElement;
  const rect = target.getBoundingClientRect();
  footerLinks.open = true;
  footerLinks.pos.x = rect.right;
  footerLinks.pos.y = rect.bottom;
}

function onFooterContextMenuClose() {
  footerLinks.open = false;
}
</script>

<style lang="scss">
.manager-sidebar {
  position: relative;
  height: 100%;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);
  overflow: visible;
  background-color: var(--color-background-deep);
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: min-content minmax(0, 1fr);
  grid-template-areas: "switcher" "main";

  &__main {
    grid-area: main;
    padding-top: 1.5rem;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__workspaceswitcher {
    &__button {
      position: relative;
      z-index: 2;

      background-color: var(--color-background-close);
      width: 100%;
      border: none;
      // border: 1px solid var(--color-action);
      border-radius: 0;
      border-top-right-radius: 0;
      border-top-left-radius: 0;
      border-bottom-right-radius: 0.5rem;
      padding: 1rem 1rem;
      text-align: left;
      align-items: center;

      box-shadow: 0 3px 5px -3px rgba(0, 0, 0, 0.3),
        inset 0 0 0 -3px var(--color-shadow-1);
      //border-left: 5px solid transparent;
      font-size: 1em;
      font-family: var(--font-family-ui);
      font-weight: 600;
      cursor: pointer;
      transition: box-shadow 200ms ease, color 200ms;

      &.-is-open {
        //box-shadow: none;
        background-color: var(--color-background-shallow);
      }

      &:hover {
        color: var(--color-action);
        box-shadow: 0 4px 5px -2px rgba(0, 0, 0, 0.3),
          inset 0 -3px 3px -3px var(--color-shadow-1);

        background-color: var(--color-background-close-hover);
      }
    }

    &__icon {
      // margin-bottom: -0.125em;
      font-size: 20px;
      margin-top: -2px;
      margin-bottom: -2px;
    }

    &__list {
      grid-area: main;
      padding-top: 1.5rem;
      z-index: 1;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: var(--color-background-deep);
      overflow: auto;
    }
  }

  &__block {
    border-bottom: 1px solid var(--color-border);
    padding: 1rem 0;
  }

  &__subheading {
    padding-left: 1rem;
    color: var(--color-text-medium);
    border-left: 5px solid transparent;
    font-weight: 600;
    font-size: 14px;
  }

  &__image {
    display: block;
    width: 100%;
  }

  &__footer {
    padding-top: 1.5rem;
    margin-top: auto;
    max-width: 20rem;
  }
  &__footerbar {
    background-color: var(--color-action);
    padding: 2px 1rem;
    padding-bottom: 0.5rem;
    font-size: 12px;
    display: flex;
    gap: 0.5rem;
    color: var(--color-action-contrast);
    max-width: 100%;

    a {
      color: var(--color-action-contrast);
      font-weight: 400;
    }
  }
  &__footerextrabutton {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.5rem;
    background-color: transparent;
    border: none;
    color: var(--color-action-contrast);
    margin-left: auto;
    svg {
      height: 1em;
      width: 1.25em;
      pointer-events: none;
    }
  }

  &__message {
    padding-left: 1rem;
    padding-right: 1rem;
    border-left: 5px solid transparent;
    font-size: 13px;
    color: var(--color-text-medium);
  }

  &__noworkspaces {
    display: flex;
    gap: 1rem;
  }

  &__icontext {
    display: grid;
    grid-template-columns: 1.5rem minmax(0, 1fr);
    gap: 0.75rem;

    & > span {
      // words that are too long should be broken
      overflow-wrap: break-word;
      hyphens: auto;
    }
  }
}
</style>
